<script setup lang="ts">
  import { useUserStore } from '@bv/stores';
  import { ref, watch } from 'vue';

  import ConversationUsersApi from '@/api/conversations/conversationUsers.api';
  import { useRealtimeUserStatusStore } from '@/stores/realtime-user-status.store';

  const userStore = useUserStore();
  const realtimeStatusStore = useRealtimeUserStatusStore();

  const currentStatus = ref<number | undefined>(
    realtimeStatusStore.lastChange?.current.status ?? userStore.userDetails?.userStatus,
  );

  watch(
    () => userStore.userDetails?.userStatus,
    (val) => {
      if (val !== undefined) {
        currentStatus.value = val;
      }
    },
    { immediate: true },
  );

  const statusData = [
    { title: 'Online', value: 0, color: '--green-500' },
    { title: 'Busy', value: 1, color: '--warning-500' },
    { title: 'Offline', value: 2, color: '--gray-500' },
  ];

  async function changeUserStatus(status: any) {
    const user = userStore.userDetails;
    if (!user || currentStatus.value === status) {
      return;
    }

    currentStatus.value = status;

    const id = user.id;
    await ConversationUsersApi.update(String(id), { userStatus: status });

    userStore.updateUserStatus(status);
    realtimeStatusStore.upsert(String(id), {
      status,
      lastStatusChangedAt: new Date().toISOString(),
    });
  }
</script>

<template>
  <div
    v-for="status in statusData"
    :key="status.value"
    class="flex items-center gap-2 p-1 text-xs text-gray-400 rounded-lg hover:bg-base-50 cursor-pointer"
    :class="{ 'bg-base-50': status.value == currentStatus }"
    @click.stop="changeUserStatus(status.value)"
  >
    <span
      class="size-3.5 shrink-0 rounded-full"
      :style="`background: var(${status.color})`"
    />
    {{ status.title }}
  </div>
</template>
